import { type FC, type RefObject, useMemo } from 'react';
import { Button, HStack, type TestProps, VStack } from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import { ChangeVariantPriceForm } from './components/ChangeVariantPriceForm';
import { ManualDiscountPriceField } from './components/ManualDiscountPriceField';
import { MinimalPriceField } from './components/MinimalPriceField';
import { PriceField } from './components/PriceField';
import { PromotionPriceField } from './components/PromotionPriceField';
import { SaveButton } from './components/SaveButton';
import { Tooltip } from '../../ui/Tooltip';
import { getProductFormId } from './model';
import { type ChangedVariantPrice, type ChangedVariantPriceOnSave } from './types';

type ChangeVariantPriceTooltipProps = {
    triggerRef: RefObject<HTMLElement>;
    isOpen: boolean;
    variant: ChangedVariantPrice;
    onClose: () => void;
    onSave: ChangedVariantPriceOnSave;
} & TestProps;

export const ChangeVariantPriceTooltip: FC<ChangeVariantPriceTooltipProps> = ({
    triggerRef,
    isOpen,
    onClose,
    variant,
    onSave,
    'data-testid': dataTestId = 'ChangeVariantPriceTooltip',
}) => {
    const formId = useMemo(() => getProductFormId(variant.product_id, variant.id), [variant.product_id, variant.id]);
    const { value: isSaving, toggleOn: startSaving, toggleOff: stopSaving } = useToggle();

    return (
        <Tooltip
            isOpen={isOpen}
            onClose={onClose}
            triggerRef={triggerRef}
            placement={['right', 'left']}
            data-testid={dataTestId}
            //Для больших чисел увеличиваем ширину тултипа, в поле умещается спокойно 7 цифр. Поле не редактируемое
            maxWidth={(variant.manual_discount_price?.length ?? 0) > 6 ? '480' : '360'}
            customContent={
                <ChangeVariantPriceForm variant={variant} formId={formId}>
                    <VStack gap="12">
                        <PriceField formId={formId} isSaving={isSaving} data-testid={`${dataTestId}.FinalPriceField`} />
                        <ManualDiscountPriceField
                            formId={formId}
                            isSaving={isSaving}
                            data-testid={`${dataTestId}.ManualDiscountPriceField`}
                        />
                        <PromotionPriceField
                            formId={formId}
                            isSaving={isSaving}
                            data-testid={`${dataTestId}.PromotionPriceField`}
                        />
                        <MinimalPriceField
                            formId={formId}
                            isSaving={isSaving}
                            data-testid={`${dataTestId}.MinimalPriceField`}
                        />
                        <HStack gap="8" justifyContent="flex-end">
                            <SaveButton
                                formId={formId}
                                isSaving={isSaving}
                                startSaving={startSaving}
                                stopSaving={stopSaving}
                                onSave={onSave}
                                onSuccessSave={onClose}
                            />
                            <Button onClick={onClose} color="text" size="xs" data-testid={`${dataTestId}.CancelButton`}>
                                Отмена
                            </Button>
                        </HStack>
                    </VStack>
                </ChangeVariantPriceForm>
            }
        />
    );
};
